import type { BoardTile, GameState, TileValue } from "./types";
import type { MoveOutcome } from "./types";

export const BOARD_SIZE = 5;
export const HAND_SIZE = 3;
export const MIN_TILE_VALUE = 1;
export const MAX_TILE_VALUE = 9;
export const CLEAR_SUM_TARGET = 10;

export type MoveResolution = {
  sum: number;
  outcome: MoveOutcome;
  clearsTile: boolean;
  builtValue: TileValue | null;
};

export function resolveMove(handTile: TileValue, boardTile: TileValue): MoveResolution {
  const sum = handTile + boardTile;

  if (handTile < 0) {
    if (sum < 0) {
      return {
        sum,
        outcome: "illegal",
        clearsTile: false,
        builtValue: null
      };
    }

    if (sum === 0) {
      return {
        sum,
        outcome: "negative_clear",
        clearsTile: true,
        builtValue: null
      };
    }

    return {
      sum,
      outcome: "negative_reduce",
      clearsTile: false,
      builtValue: sum
    };
  }

  if (handTile === boardTile) {
    return {
      sum,
      outcome: "match_clear",
      clearsTile: true,
      builtValue: null
    };
  }

  if (sum === CLEAR_SUM_TARGET) {
    return {
      sum,
      outcome: "build_to_ten_clear",
      clearsTile: true,
      builtValue: null
    };
  }

  if (sum < CLEAR_SUM_TARGET) {
    return {
      sum,
      outcome: "build",
      clearsTile: false,
      builtValue: sum
    };
  }

  return {
    sum,
    outcome: "illegal",
    clearsTile: false,
    builtValue: null
  };
}

export function isLegalMove(handTile: TileValue, boardTile: TileValue): boolean {
  return resolveMove(handTile, boardTile).outcome !== "illegal";
}

export function collapseBoardLeft(board: BoardTile[], indexToRemove: number): BoardTile[] {
  return board.filter((_, index) => index !== indexToRemove);
}

export function getLegalMoveIndexesForHand(
  state: Pick<GameState, "board">,
  handTile: TileValue
): number[] {
  return state.board.reduce<number[]>((indexes, boardTile, index) => {
    if (isLegalMove(handTile, boardTile.value)) {
      indexes.push(index);
    }
    return indexes;
  }, []);
}

export function getAnyLegalMoveIndexes(state: Pick<GameState, "board" | "hand">): number[] {
  return state.board.reduce<number[]>((indexes, boardTile, index) => {
    const hasLegalHandTile = state.hand.some((handTile) => isLegalMove(handTile, boardTile.value));
    if (hasLegalHandTile) {
      indexes.push(index);
    }
    return indexes;
  }, []);
}

export function hasAnyLegalMove(state: Pick<GameState, "board" | "hand">): boolean {
  return state.hand.some((handTile) => getLegalMoveIndexesForHand(state, handTile).length > 0);
}

export function nextBestRound(state: Pick<GameState, "bestRound" | "roundNumber">): number {
  return Math.max(state.bestRound, state.roundNumber);
}
